
import { useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, BarChart3, Layout, Play, MessageCircle, Car, Loader2 } from 'lucide-react';
import Section from '../ui/Section';
import DemoContainer from '../demos/DemoContainer';

// Lazy load dos demos para reduzir bundle inicial
const LeadQualificationDemo = lazy(() => import('../demos/LeadQualificationDemo'));
const InteractiveDashboardDemo = lazy(() => import('../demos/InteractiveDashboardDemo'));
const ManagementSystemDemo = lazy(() => import('../demos/ManagementSystemDemo'));
const ChatManagerDemo = lazy(() => import('../demos/ChatManagerDemo'));
const VehicleSearchDemo = lazy(() => import('../demos/VehicleSearchDemo'));

const DemoLoader = () => (
  <div className="flex items-center justify-center h-full">
    <Loader2 className="w-8 h-8 text-primary animate-spin" />
  </div>
);

const Projects = () => {
  const { t } = useTranslation();
  const [activeDemo, setActiveDemo] = useState<string | null>(null);
  
  const demos = [
    {
      id: 'lead-agent',
      titleKey: 'leadAgent',
      icon: <MessageSquare className="w-8 h-8" />,
      gradient: 'from-primary to-secondary',
      tags: ['Agno', 'Gemini', 'Python'],
    },
    {
      id: 'dashboard',
      titleKey: 'dashboard',
      icon: <BarChart3 className="w-8 h-8" />,
      gradient: 'from-secondary to-primary',
      tags: ['React', 'Recharts', 'TypeScript'],
    },
    {
      id: 'management',
      titleKey: 'management',
      icon: <Layout className="w-8 h-8" />,
      gradient: 'from-primary to-accent-moss',
      tags: ['FastAPI', 'PostgreSQL', 'React'],
    },
    {
      id: 'chat-manager',
      titleKey: 'chatManager',
      icon: <MessageCircle className="w-8 h-8" />,
      gradient: 'from-accent-moss to-accent-sage',
      tags: ['React', 'WebSocket', 'Real-time'],
    },
    {
      id: 'vehicle-search',
      titleKey: 'vehicleSearch',
      icon: <Car className="w-8 h-8" />,
      gradient: 'from-accent-sage to-secondary',
      tags: ['React', 'SQLite', 'Streamlit'],
    },
  ];
  
  return (
    <>
      <Section id="projects">
        {/* Header simples */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('demos.title')}
          </h2>
          <p className="text-lg text-gray-400">
            {t('demos.subtitle')}
          </p>
        </div>
        
        {/* Grid limpo */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {demos.map((demo) => (
            <div
              key={demo.id}
              className="group bg-white/10 border border-white/10 rounded-2xl overflow-hidden hover:border-primary/50 transition-all hover:shadow-xl hover:shadow-primary/10"
            >
              {/* Header do card */}
              <div className={`relative h-48 bg-gradient-to-br ${demo.gradient} flex items-center justify-center`}>
                <div className="text-white opacity-90 group-hover:scale-110 transition-transform">
                  {demo.icon}
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  {t(`demos.${demo.titleKey}.title`)}
                </h3>
                
                <p className="text-gray-400 mb-4 text-sm leading-relaxed">
                  {t(`demos.${demo.titleKey}.description`)}
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {demo.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs font-medium rounded-lg bg-white/10 text-gray-400 border border-white/10"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                {/* CTA */}
                <button
                  onClick={() => setActiveDemo(demo.id)}
                  className="w-full px-4 py-3 bg-primary hover:bg-primary-light text-background font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  <span>{t('demos.tryDemo')}</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Demo Modals */}
      <DemoContainer
        title={t('demos.leadAgent.title')}
        description={t('demos.leadAgent.fullDescription')}
        isOpen={activeDemo === 'lead-agent'}
        onClose={() => setActiveDemo(null)}
      >
        <Suspense fallback={<DemoLoader />}>
          <LeadQualificationDemo />
        </Suspense>
      </DemoContainer>

      <DemoContainer
        title={t('demos.dashboard.title')}
        description={t('demos.dashboard.fullDescription')}
        isOpen={activeDemo === 'dashboard'}
        onClose={() => setActiveDemo(null)}
      >
        <Suspense fallback={<DemoLoader />}>
          <InteractiveDashboardDemo />
        </Suspense>
      </DemoContainer>

      <DemoContainer
        title={t('demos.management.title')}
        description={t('demos.management.fullDescription')}
        isOpen={activeDemo === 'management'}
        onClose={() => setActiveDemo(null)}
      >
        <Suspense fallback={<DemoLoader />}>
          <ManagementSystemDemo />
        </Suspense>
      </DemoContainer>

      <DemoContainer
        title={t('demos.chatManager.title')}
        description={t('demos.chatManager.fullDescription')}
        isOpen={activeDemo === 'chat-manager'}
        onClose={() => setActiveDemo(null)}
      >
        <Suspense fallback={<DemoLoader />}>
          <ChatManagerDemo />
        </Suspense>
      </DemoContainer>

      <DemoContainer
        title={t('demos.vehicleSearch.title')}
        description={t('demos.vehicleSearch.fullDescription')}
        isOpen={activeDemo === 'vehicle-search'}
        onClose={() => setActiveDemo(null)}
      >
        <Suspense fallback={<DemoLoader />}>
          <VehicleSearchDemo />
        </Suspense>
      </DemoContainer>
    </>
  );
};

export default Projects;
